import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BillsService } from "services/bills";
import { AccountsService } from "services/accounts";
import {
  FileText, CheckCircle, XCircle, ExternalLink, Loader2, AlertCircle, Search,
} from "lucide-react";

const CARD = { background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 16 };

const STATUS_LABEL = {
  PENDING:    { label: "Sin clasificar",  bg: "#fef3c7", color: "#92400e" },
  CLASSIFIED: { label: "Pre-clasificado", bg: "#dbeafe", color: "#1e40af" },
  POSTED:     { label: "Aprobado",        bg: "#dcfce7", color: "#166534" },
  REJECTED:   { label: "Rechazado",       bg: "#fee2e2", color: "#991b1b" },
};

function fmtCLP(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);
}

export default function BillsPending(props) {
  // Si recibe memberId por prop (cuando se monta como tab de /admin/accounting)
  // lo usa; sino lo lee del query string (modo standalone).
  const [searchParams, setSearchParams] = useSearchParams();
  const memberId = props?.memberId || searchParams.get("memberId") || "";
  const initialStatus = props?.status || searchParams.get("status") || "CLASSIFIED";
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const changeStatus = (s) => {
    setStatusFilter(s);
    if (!props?.memberId) setSearchParams({ memberId, status: s });
  };

  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [viewUrl, setViewUrl] = useState(null);
  const [editing, setEditing] = useState(null);
  const [approving, setApproving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      const [billsRes, accountsRes] = await Promise.all([
        BillsService.list(memberId, { status: statusFilter, limit: 200 }),
        AccountsService ? AccountsService.list(memberId).catch(() => []) : Promise.resolve([]),
      ]);
      setBills(billsRes.bills || []);
      setAccounts(Array.isArray(accountsRes) ? accountsRes : (accountsRes.accounts || []));
    } catch (err) {
      console.error(err);
      setMessage({ kind: "error", text: err?.response?.data?.error || err.message });
    } finally {
      setLoading(false);
    }
  }, [memberId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const gastoAccounts = useMemo(
    () => (accounts || []).filter((a) => a.type === "GASTO" && a.isActive !== false),
    [accounts]
  );

  const openBill = async (bill) => {
    setSelected(bill);
    setViewUrl(null);
    setEditing({ ...bill });
    try {
      const { viewUrl } = await BillsService.get(memberId, bill._id);
      setViewUrl(viewUrl);
    } catch (err) {
      console.error(err);
    }
  };

  const closeModal = () => {
    setSelected(null);
    setViewUrl(null);
    setEditing(null);
  };

  const updateField = (path, value) => {
    setEditing((e) => {
      const copy = { ...e };
      const keys = path.split(".");
      let cur = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...(cur[keys[i]] || {}) };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  const handleApprove = async ({ withNewAccount = false } = {}) => {
    const suggested = selected?.extracted?.suggestedNewAccount;
    if (!editing.accountCode && !withNewAccount) {
      alert("Selecciona una cuenta antes de aprobar");
      return;
    }
    setApproving(true);
    try {
      const payload = {
        accountCode: withNewAccount ? suggested.code : editing.accountCode,
        amount: parseFloat(editing.amount) || 0,
        neto: parseFloat(editing.neto) || 0,
        iva: parseFloat(editing.iva) || 0,
        exento: parseFloat(editing.exento) || 0,
        type: editing.type,
        paymentMethod: editing.paymentMethod || "CAJA",
        supplier: editing.supplier,
        description: editing.description,
        date: editing.date,
      };
      if (withNewAccount && suggested) {
        payload.createAccount = {
          code: suggested.code,
          name: suggested.name,
          parentCode: suggested.parentCode,
        };
      }
      const res = await BillsService.approve(memberId, selected._id, payload);
      setMessage({ kind: "ok", text: `Aprobado · asiento ${res.entry?._id?.slice(-6) || ""}` });
      closeModal();
      await load();
    } catch (err) {
      setMessage({ kind: "error", text: err?.response?.data?.error || err.message });
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    const reason = window.prompt("¿Por qué rechazas este gasto?", "");
    if (reason == null) return;
    try {
      await BillsService.reject(memberId, selected._id, reason);
      setMessage({ kind: "ok", text: "Bill rechazado" });
      closeModal();
      await load();
    } catch (err) {
      setMessage({ kind: "error", text: err?.response?.data?.error || err.message });
    }
  };

  if (!memberId) {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: "#64748b" }}>Falta el parámetro <code>memberId</code> en la URL.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>Gastos por aprobar</h1>
          <p style={{ fontSize: 13, color: "#64748b" }}>Member: <code>{memberId}</code></p>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          {["CLASSIFIED", "PENDING", "POSTED", "REJECTED"].map((s) => (
            <button
              key={s}
              onClick={() => changeStatus(s)}
              style={{
                padding: "6px 12px", borderRadius: 6, border: "1px solid #e2e8f0",
                background: statusFilter === s ? "#0353a4" : "#fff",
                color: statusFilter === s ? "#fff" : "#334155",
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
            >
              {STATUS_LABEL[s]?.label || s}
            </button>
          ))}
        </div>
      </div>

      {message && (
        <div style={{
          padding: 12, marginBottom: 12, borderRadius: 8,
          background: message.kind === "ok" ? "#ecfdf5" : "#fef2f2",
          color: message.kind === "ok" ? "#065f46" : "#991b1b",
          fontSize: 13,
        }}>
          {message.text}
        </div>
      )}

      <div style={CARD}>
        {loading ? (
          <p style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>Cargando...</p>
        ) : bills.length === 0 ? (
          <p style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>Sin gastos en este estado.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e2e8f0", textAlign: "left" }}>
                <th style={{ padding: "8px 6px" }}>Fecha</th>
                <th style={{ padding: "8px 6px" }}>Proveedor</th>
                <th style={{ padding: "8px 6px" }}>RUT</th>
                <th style={{ padding: "8px 6px" }}>Tipo</th>
                <th style={{ padding: "8px 6px", textAlign: "right" }}>Monto</th>
                <th style={{ padding: "8px 6px" }}>Cuenta sugerida</th>
                <th style={{ padding: "8px 6px" }}>IA</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b) => {
                const conf = b.extracted?.confidence;
                return (
                  <tr key={b._id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "10px 6px" }}>{b.date ? new Date(b.date).toLocaleDateString("es-CL") : "—"}</td>
                    <td style={{ padding: "10px 6px", fontWeight: 500 }}>{b.supplier?.name || "—"}</td>
                    <td style={{ padding: "10px 6px", color: "#64748b" }}>{b.supplier?.rut || "—"}</td>
                    <td style={{ padding: "10px 6px" }}>{b.type}</td>
                    <td style={{ padding: "10px 6px", textAlign: "right", fontWeight: 600 }}>{fmtCLP(b.amount)}</td>
                    <td style={{ padding: "10px 6px" }}>{b.accountCode || "—"}</td>
                    <td style={{ padding: "10px 6px", color: "#64748b" }}>
                      {b.classifiedBy === "AUTO" && conf != null ? `${Math.round(conf * 100)}%` : "—"}
                    </td>
                    <td style={{ padding: "10px 6px", textAlign: "right" }}>
                      <button
                        onClick={() => openBill(b)}
                        style={{
                          padding: "4px 10px", border: "1px solid #cbd5e1", borderRadius: 6,
                          background: "#fff", cursor: "pointer", fontSize: 12,
                        }}
                      >
                        Revisar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de revisión / aprobación */}
      {selected && editing && (
        <div
          onClick={closeModal}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 12, maxWidth: 1100, width: "100%",
              maxHeight: "92vh", display: "flex", flexDirection: "column", overflow: "hidden",
            }}
          >
            <div style={{ padding: 16, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 600 }}>Revisar gasto</h2>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {selected.classifiedBy === "AUTO" && `🤖 Sugerencia IA · ${selected.accountCode}`}
                  {selected.extracted?.confidence != null && ` · ${Math.round(selected.extracted.confidence * 100)}% confianza`}
                </div>
              </div>
              <button onClick={closeModal} style={{ background: "transparent", border: "none", fontSize: 24, cursor: "pointer" }}>×</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", flex: 1, overflow: "hidden" }}>
              {/* Preview */}
              <div style={{ background: "#f8fafc", overflow: "auto" }}>
                {viewUrl ? (
                  selected.file?.contentType?.startsWith("image/") ? (
                    <img src={viewUrl} alt="" style={{ width: "100%" }} />
                  ) : (
                    <iframe src={viewUrl} title="preview" style={{ width: "100%", height: "100%", border: "none" }} />
                  )
                ) : (
                  <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Cargando preview...</div>
                )}
              </div>

              {/* Edit form */}
              <div style={{ padding: 16, borderLeft: "1px solid #e5e7eb", overflow: "auto", fontSize: 13 }}>
                <Field label="Proveedor">
                  <input type="text" value={editing.supplier?.name || ""}
                    onChange={(e) => updateField("supplier.name", e.target.value)}
                    style={inputStyle} />
                </Field>
                <Field label="RUT">
                  <input type="text" value={editing.supplier?.rut || ""}
                    onChange={(e) => updateField("supplier.rut", e.target.value)}
                    style={inputStyle} />
                </Field>
                <Field label="Fecha">
                  <input type="date" value={editing.date ? new Date(editing.date).toISOString().slice(0, 10) : ""}
                    onChange={(e) => updateField("date", e.target.value)}
                    style={inputStyle} />
                </Field>
                <Field label="Tipo">
                  <select value={editing.type || "BOLETA"} onChange={(e) => updateField("type", e.target.value)} style={inputStyle}>
                    <option>BOLETA</option><option>FACTURA</option><option>HONORARIOS</option><option>OTRO</option>
                  </select>
                </Field>
                <Field label="Monto total">
                  <input type="number" value={editing.amount || ""} onChange={(e) => updateField("amount", e.target.value)} style={inputStyle} />
                </Field>
                <Field label="Neto">
                  <input type="number" value={editing.neto || ""} onChange={(e) => updateField("neto", e.target.value)} style={inputStyle} />
                </Field>
                <Field label="IVA">
                  <input type="number" value={editing.iva || ""} onChange={(e) => updateField("iva", e.target.value)} style={inputStyle} />
                </Field>
                <Field label="Cuenta de gasto">
                  <select value={editing.accountCode || ""} onChange={(e) => updateField("accountCode", e.target.value)} style={inputStyle}>
                    <option value="">— seleccionar —</option>
                    {gastoAccounts.map((a) => (
                      <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Método de pago">
                  <select value={editing.paymentMethod || "CAJA"} onChange={(e) => updateField("paymentMethod", e.target.value)} style={inputStyle}>
                    <option>CAJA</option><option>BANCO</option><option>CREDITO</option>
                  </select>
                </Field>
                <Field label="Descripción">
                  <input type="text" value={editing.description || ""} onChange={(e) => updateField("description", e.target.value)} style={inputStyle} />
                </Field>

                {selected.extracted?.reason && (
                  <div style={{ marginTop: 8, padding: 8, background: "#eff6ff", borderRadius: 6, fontSize: 11, color: "#1e40af" }}>
                    <strong>IA:</strong> {selected.extracted.reason}
                  </div>
                )}

                {selected.extracted?.suggestedNewAccount && !editing.accountCode && (
                  <div style={{ marginTop: 8, padding: 10, background: "#fef3c7", border: "1px dashed #f59e0b", borderRadius: 6, fontSize: 12 }}>
                    <div style={{ fontWeight: 600, color: "#92400e", marginBottom: 4 }}>
                      💡 IA sugiere crear cuenta nueva
                    </div>
                    <div style={{ color: "#78350f", fontSize: 12 }}>
                      <code>{selected.extracted.suggestedNewAccount.code}</code> — {selected.extracted.suggestedNewAccount.name}<br/>
                      <span style={{ fontSize: 11, color: "#a16207" }}>parent: {selected.extracted.suggestedNewAccount.parentCode}</span>
                    </div>
                    {selected.extracted.suggestedNewAccount.reason && (
                      <div style={{ fontSize: 11, fontStyle: "italic", color: "#92400e", marginTop: 4 }}>
                        {selected.extracted.suggestedNewAccount.reason}
                      </div>
                    )}
                    <button
                      onClick={() => handleApprove({ withNewAccount: true })}
                      disabled={approving}
                      style={{
                        marginTop: 8, padding: "4px 10px", borderRadius: 6,
                        background: "#d97706", color: "#fff", border: "none", fontSize: 11,
                        fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      Crear cuenta + aprobar
                    </button>
                  </div>
                )}

                {/* Info de aprobación/rechazo (estados terminales) */}
                {selected.status === "POSTED" && (
                  <div style={{ marginTop: 12, padding: 10, background: "#dcfce7", border: "1px solid #86efac", borderRadius: 6, fontSize: 12, color: "#166534" }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      <CheckCircle size={12} style={{ verticalAlign: "middle" }} /> Aprobado
                    </div>
                    {selected.approvedBy && (
                      <div>Por {selected.approvedBy.name || selected.approvedBy.email}</div>
                    )}
                    {selected.approvedAt && (
                      <div>El {new Date(selected.approvedAt).toLocaleString("es-CL")}</div>
                    )}
                    {selected.journalEntryId && (
                      <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px dashed #86efac" }}>
                        Asiento contable:
                        <div style={{ fontFamily: "monospace", fontSize: 11 }}>
                          {typeof selected.journalEntryId === "object" ? (
                            <>
                              {new Date(selected.journalEntryId.date).toLocaleDateString("es-CL")} · {selected.journalEntryId.description}
                              <br />Debe ${Math.round(selected.journalEntryId.totalDebit).toLocaleString("es-CL")} = Haber ${Math.round(selected.journalEntryId.totalCredit).toLocaleString("es-CL")}
                            </>
                          ) : (
                            `#${String(selected.journalEntryId).slice(-8)}`
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {selected.status === "REJECTED" && (
                  <div style={{ marginTop: 12, padding: 10, background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, fontSize: 12, color: "#991b1b" }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      <XCircle size={12} style={{ verticalAlign: "middle" }} /> Rechazado
                    </div>
                    {selected.rejectedReason && (
                      <div style={{ fontStyle: "italic" }}>Razón: {selected.rejectedReason}</div>
                    )}
                  </div>
                )}

                <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                  {selected.status !== "POSTED" && selected.status !== "REJECTED" && (
                    <>
                      <button
                        onClick={() => handleApprove()}
                        disabled={approving}
                        style={{
                          flex: 1, padding: "8px 12px", border: "none", borderRadius: 6,
                          background: approving ? "#94a3b8" : "#16a34a", color: "#fff",
                          fontSize: 13, fontWeight: 600, cursor: approving ? "not-allowed" : "pointer",
                          display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
                        }}
                      >
                        {approving ? <Loader2 size={14} className="spin" /> : <CheckCircle size={14} />}
                        Aprobar y postear
                      </button>
                      <button
                        onClick={handleReject}
                        style={{
                          padding: "8px 12px", border: "1px solid #fca5a5", borderRadius: 6,
                          background: "#fff", color: "#dc2626", fontSize: 13, fontWeight: 600, cursor: "pointer",
                          display: "inline-flex", alignItems: "center", gap: 4,
                        }}
                      >
                        <XCircle size={14} /> Rechazar
                      </button>
                    </>
                  )}
                  {selected.status === "POSTED" && (
                    <div style={{ flex: 1, padding: 8, background: "#dcfce7", color: "#166534", textAlign: "center", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                      <CheckCircle size={14} style={{ verticalAlign: "middle" }} /> Posteado al libro diario
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6,
  fontSize: 13, fontFamily: "inherit",
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: "block", fontSize: 10, color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.5, marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
